import { useCallback } from 'react';
import { Modal, Badge, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InlineEdit } from '@/components/common/InlineEdit';
import { StatusBadge, TextMutedSmall } from '@/styles/shared.styled';
import { DefinitionList } from '@/components/common/DefinitionList';
import { useDateFormat } from '@/utils/dates';
import { useGetSprawaQuery, useUpdateSprawaMyStatusMutation, useListSprawaStatusDefinitionsQuery } from '@/store/sprawyApi';

interface Props {
  sprawaId: string | null;
  onClose: () => void;
}

export default function SprawaDetailModal({ sprawaId, onClose }: Props) {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const { data: sprawa, isLoading } = useGetSprawaQuery(sprawaId!, { skip: !sprawaId });
  const { data: statusDefinitions } = useListSprawaStatusDefinitionsQuery();
  const [updateStatus] = useUpdateSprawaMyStatusMutation();

  const handleStatusChange = useCallback(async (statusDefinitionId: string | number | boolean) => {
    if (sprawaId && statusDefinitionId) {
      await updateStatus({ sprawaId, statusDefinitionId: String(statusDefinitionId) }).unwrap();
    }
  }, [sprawaId, updateStatus]);

  return (
    <Modal show={!!sprawaId} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isLoading ? <Spinner size="sm" /> : sprawa?.signature}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading || !sprawa ? (
          <div className="text-center py-4"><Spinner /></div>
        ) : (
          <>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Badge bg="secondary">{t(`sprawaStatus.${sprawa.portalStatus}`)}</Badge>
              {sprawa.statusName && (
                <StatusBadge $color={sprawa.statusColor ?? undefined}>{sprawa.statusName}</StatusBadge>
              )}
              <InlineEdit
                value={sprawa.statusDefinitionId ?? ''}
                onSave={handleStatusChange}
                variant="select"
                options={statusDefinitions?.map((sd) => ({ value: sd.id, label: sd.name })) ?? []}
                ariaLabel={t('sprawy.form.myStatus')}
              />
            </div>

            <DefinitionList
              maxWidth="500px"
              items={[
                { label: t('sprawy.form.court'), value: sprawa.court },
                { label: t('sprawy.form.department'), value: sprawa.department },
                { label: t('sprawy.form.judge'), value: sprawa.judge },
                { label: t('sprawy.form.subject'), value: sprawa.subject },
                { label: t('sprawy.form.receiptDate'), value: sprawa.receiptDate ? formatDate(sprawa.receiptDate) : null },
                { label: t('sprawy.form.finishDate'), value: sprawa.finishDate ? formatDate(sprawa.finishDate) : null },
                { label: t('sprawy.form.value'), value: sprawa.value },
              ]}
            />

            {sprawa.parties.length > 0 && (
              <div className="mt-3">
                <h6 className="text-muted">{t('sprawy.detail.parties')}</h6>
                <ul className="mb-0">
                  {sprawa.parties.map((p) => (
                    <li key={p.id}>
                      <Link to={`/customers/${p.customerId}`} onClick={onClose}>{p.customerName}</Link>
                      {p.role && <span className="text-muted ms-1">({p.role})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-3 d-flex gap-3">
              <TextMutedSmall as="span">{sprawa.documentCount} {t('sprawy.table.documents')}</TextMutedSmall>
              <TextMutedSmall as="span">{sprawa.posiedzeniaCount} {t('sprawy.table.posiedzenia')}</TextMutedSmall>
              <Link to={`/sprawy/${sprawa.id}`} onClick={onClose}>{t('sprawy.detail.info')} →</Link>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
